import { useCallback, useEffect, useRef, useState } from "react";
import { getArticlesAdmin } from "../../components/Artigos/articlesService";
import { Plus } from "lucide-react";

interface Article {
  id: number;
  title: string;
  content: string;
  image?: string;
  created_at: string;
}

const ARTICLES_PER_PAGE = 5;

export default function GetArticles() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const lastArticleRef = useRef<HTMLDivElement>(null);
  const dropDownRef = useRef<HTMLDivElement>(null);
  const limit = ARTICLES_PER_PAGE;
  const imageBaseUrl = "http://localhost:5452/uploads/artigos/";

  const loadArticles = useCallback(async (pageNumber: number = 1) => {
    if (!hasMore && pageNumber > 1) return;

    try {
      setLoading(true);
      const accessToken = localStorage.getItem('@seculos:token');
      const token = accessToken ? JSON.parse(accessToken).accessToken : null;

      if(!token){
        console.log("Nenhum token encontrado!");
        setHasMore(false);
        return;
      }

      const response = await getArticlesAdmin(pageNumber, limit, token);

      if (!response || !response.data || response.data.length === 0) {
        setHasMore(false);
        return;
      }

      if (pageNumber === 1) {
        setArticles(response.data);
      } else {
        setArticles(prev => [...prev, ...response.data]);
      }

      setPage(pageNumber);
      setHasMore(response.hasMore);

      console.log("Artigos carregados - Página:", pageNumber, response.data);
    } catch (error) {
      console.error("Erro ao buscar artigos:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [hasMore, limit]);

  // Dropdown externo
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropDownRef.current && event.target instanceof Node && !dropDownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = (articleId: number) => {
    setOpenDropdown(openDropdown === articleId ? null : articleId);
  };

  const setupObserver = useCallback((node: HTMLDivElement) => {
    if (loading || !hasMore) return;

    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        loadArticles(page + 1);
      }
    }, { threshold: 0.1 });

    if (node) observer.current.observe(node);
  }, [loading, hasMore, page, loadArticles]);

  useEffect(() => {
    if (lastArticleRef.current && articles.length > 0 && hasMore) {
      setupObserver(lastArticleRef.current);
    } else if (!hasMore && observer.current) {
      observer.current.disconnect();
    }

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [articles, hasMore, setupObserver]);

  const handleEdit = (articleId: number) => {
    console.log("Editar artigo:", articleId);
    setOpenDropdown(null);
  };

  const handleDelete = (articleId: number) => {
    console.log("Deletar artigo:", articleId);
    setOpenDropdown(null);
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  });

  useEffect(() => {
    loadArticles();
  }, []);

  return (
    <div>
      <div className="flex-col items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Gestão de Artigos</h2>
        <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 mt-4 text-gray-50 px-4 py-2 rounded-lg shadow">
          <Plus size={18} /> Criar Novo Artigo
        </button>
      </div>
      <h2 className="text-xl">Lista de Artigos</h2>
      <div className="space-y-3">
        {articles.map((article, index) => (
          <div
            key={article.id}
            ref={index === articles.length - 1 ? lastArticleRef : undefined}
            className="bg-white rounded-lg shadow-md mt-1 p-6 hover:shadow-lg transition-shadow duration-300"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                {article.image && (
                  <div className="md:w-1/4">
                    <img
                      src={`${imageBaseUrl}${article.image}`}
                      alt={article.title}
                      className="w-12 h-48 object-cover rounded-lg"
                      onError={e => {
                        (e.target as HTMLImageElement).src = "https://via.placeholder.com/400x300?text=Sem+Imagem";
                      }}
                    />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-medium text-gray-800 truncate">{article.title}</h3>
                  <p className="text-sm text-gray-500">📅 {formatDate(article.created_at)}</p>
                </div>

                <div className="relative" ref={dropDownRef}>
                  <button
                    onClick={() => toggleDropdown(article.id)}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                    title="Ações"
                  >
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"/>
                    </svg>
                  </button>
                  {openDropdown === article.id && (
                    <div className="absolute right-0 top-10 bg-white rounded-lg shadow-lg border border-gray-200 z-10 min-w-[120px]">
                      <button onClick={() => handleEdit(article.id)} className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 rounded-t-lg flex items-center gap-2">
                        ✏️ Editar
                      </button>
                      <div className="border-t border-gray-200"></div>
                      <button onClick={() => handleDelete(article.id)} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 rounded-b-lg flex items-center gap-2">
                        🗑️ Deletar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {loading && (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Carregando mais artigos...</span>
        </div>
      )}

      {!hasMore && articles.length > 0 && (
        <div className="text-center py-6 text-gray-500 border-t mt-4">🎉 Todos os artigos foram carregados!</div>
      )}

      {!loading && articles.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
          <p className="text-lg">📝 Nenhum artigo encontrado</p>
          <p className="text-sm mt-2">Comece criando seu primeiro artigo!</p>
        </div>
      )}

      {articles.length > 0 && (
        <div className="mt-4 text-sm text-gray-600 text-center">Total de artigos: {articles.length}</div>
      )}
    </div>
  );
}
